import type { NotificationTicket } from '@/types/notifications';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const AUTH_ERROR_EVENT = 'auth:unauthorized';

/**
 * The session cannot mint a ticket and never will without re-authenticating. Retrying is pointless:
 * a delegated agent-ui session has no refresh path, so once its token expires every subsequent
 * attempt returns the same 401 — which is how a single dead session ended up re-requesting a ticket
 * once a minute, forever. Callers must stop reconnecting when they see this.
 */
export class NotificationAuthError extends Error {
  constructor(readonly status: number) {
    super(`Not authorized to open the notification stream (${status})`);
    this.name = 'NotificationAuthError';
  }
}

export async function getNotificationTicket(): Promise<NotificationTicket> {
  const res = await fetch(`${API_BASE_URL}/notifications/ticket`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (res.status === 401 || res.status === 403) {
    // Same signal the other API clients raise, so the app re-runs the SSO delegation instead of
    // sitting on a dead session.
    window.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT, { detail: { status: res.status } }));
    throw new NotificationAuthError(res.status);
  }
  if (!res.ok) {
    throw new Error(`Failed to get notification ticket: ${res.status}`);
  }
  return res.json();
}

export function buildNotificationStreamUrl(ticket: string): string {
  return `${API_BASE_URL}/notifications/stream?ticket=${encodeURIComponent(ticket)}`;
}
